import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowRight, Building2, CalendarDays, Check, HandHeart, Mail, MapPin, Network, ShieldCheck, User, UsersRound } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { authApi } from '@/services/auth'
import { orgTreeApi } from '@/services/org-tree'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { getErrorMessage } from '@/lib/api'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import {
  FieldError,
  FormSection,
  IconInput,
  PasswordChecklist,
  PasswordInput,
  PhoneInput,
  SignupSplitShell,
  type PasswordRule,
  type SignupBrand,
} from '@/components/auth/signup-split-shell'

const PHONE_NUMBER_SAMPLE_PLACEHOLDER = '9876543210'

const PASSWORD_RULES: PasswordRule[] = [
  { label: '6–20 characters', test: (value) => value.length >= 6 && value.length <= 20 },
  { label: 'Upper & lowercase letters', test: (value) => /[a-z]/.test(value) && /[A-Z]/.test(value) },
  { label: 'At least one digit', test: (value) => /\d/.test(value) },
  { label: 'A symbol (_@$!%*#?&)', test: (value) => /[_@$!%*#?&]/.test(value) },
]

const COMMUNITY_BRAND: SignupBrand = {
  logo: (
    <div className="inline-flex items-center rounded-2xl bg-white px-4 py-3 shadow-lg shadow-slate-950/10">
      <img src="/communitypro-logo.png" alt="CommunityPro" className="h-9 w-auto" />
    </div>
  ),
  mobileLogo: <img src="/communitypro-logo.png" alt="CommunityPro" className="h-8 w-auto" />,
  eyebrow: 'Community management, simplified',
  headline: 'Bring your people, programmes and giving together.',
  description: 'Set up your community in minutes — memberships, fundraising and events are ready the moment you sign in.',
  highlights: [
    { icon: UsersRound, title: 'Memberships', text: 'Plans, renewals and a member directory that stays current.' },
    { icon: HandHeart, title: 'Fundraising & donations', text: 'Campaigns, donor history and receipts in one place.' },
    { icon: CalendarDays, title: 'Events', text: 'Registrations, attendance and payments without spreadsheets.' },
    { icon: Network, title: 'Multi-branch', text: 'Local teams with central oversight and role-based access.' },
  ],
  trustPoints: [
    { icon: Check, label: 'Free for small communities' },
    { icon: Check, label: 'No card required' },
    { icon: ShieldCheck, label: 'Role-based access' },
  ],
  gradientClassName: 'from-[#0869d7] via-[#0a7fd0] to-[#00a9a0]',
}

const communityCompanySignupSchema = z
  .object({
    community_name: z.string().trim().min(1, 'Community name is required'),
    owner_name: z.string().trim().min(1, 'Your name is required'),
    email: z.string().email('Please enter a valid email address'),
    phone_number: z.string().trim().min(1, 'Phone number is required'),
    country_id: z.string().trim().min(1, 'Country is required'),
    city: z.string().trim().min(1, 'City is required'),
    password: z
      .string()
      .min(6, 'Password must be 6-20 characters')
      .max(20, 'Password must be 6-20 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[_@$!%*#?&])/,
        'Password must contain uppercase, lowercase, a digit, and a symbol (_@$!%*#?&)',
      ),
    password_confirm: z.string(),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: 'Passwords do not match',
    path: ['password_confirm'],
  })

type CommunityCompanySignupFormData = z.infer<typeof communityCompanySignupSchema>

export default function SignupCommunityCompanyPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CommunityCompanySignupFormData>({
    resolver: zodResolver(communityCompanySignupSchema),
    defaultValues: {
      country_id: '94',
    },
  })

  const selectedCountryId = watch('country_id')
  const password = watch('password') ?? ''

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries'],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
  })

  const selectedCountry = countriesQuery.data?.find((country) => String(country.id) === selectedCountryId)

  useEffect(() => {
    if (selectedCountryId || !countriesQuery.data?.length) {
      return
    }

    setValue('country_id', String(countriesQuery.data[0].id), { shouldValidate: false, shouldDirty: false })
  }, [countriesQuery.data, selectedCountryId, setValue])

  const onSubmit = async (data: CommunityCompanySignupFormData) => {
    setIsLoading(true)
    try {
      const response = await authApi.communityCompanySignup({
        community_name: data.community_name,
        owner_name: data.owner_name,
        email: data.email,
        password: data.password,
        country_id: data.country_id,
        phone_number: data.phone_number,
        city: data.city,
      })

      // The signup response issues a session directly, but without the
      // available-posts list PublicRoute needs -- a plain login call (like
      // the other signup pages use) fetches posts properly so role
      // selection lands on /posts instead of bouncing to /profile.
      const loginTenant = response.data.identifier

      try {
        const loginResponse = await authApi.login({
          tenant: loginTenant,
          email: data.email,
          password: data.password,
        })

        if (loginResponse.data?.access_token) {
          toast.success('Community created successfully! Select a post to continue.')
          navigate(buildPostSelectionPath(loginResponse.data.service_instance_id || loginTenant), { replace: true })
          return
        }
      } catch {
        // Signup succeeded; fall through to the sign-in redirect.
      }

      toast.success('Community created successfully! Sign in with your community code.')
      navigate(`/login?tenant=${encodeURIComponent(loginTenant)}`)
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <SignupSplitShell
      brand={COMMUNITY_BRAND}
      title="Start your community"
      description="Create your CommunityPro account in one step. You'll be signed in straight away."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8" noValidate>
        <FormSection step={1} title="About your community">
          <div className="space-y-1.5">
            <Label htmlFor="community_name">Community name</Label>
            <IconInput id="community_name" icon={Building2} placeholder="Acme Residents Community" disabled={isLoading} {...register('community_name')} aria-invalid={!!errors.community_name} />
            <FieldError message={errors.community_name?.message} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="country_id">Country</Label>
              <input type="hidden" {...register('country_id')} />
              <Select value={selectedCountryId || ''} onValueChange={(value) => setValue('country_id', value, { shouldValidate: true, shouldDirty: true })} disabled={isLoading || countriesQuery.isLoading || !countriesQuery.data?.length}>
                <SelectTrigger id="country_id" aria-invalid={!!errors.country_id} className="!h-11 w-full rounded-xl bg-white">
                  <SelectValue placeholder={countriesQuery.isLoading ? 'Loading countries...' : 'Select a country'} />
                </SelectTrigger>
                <SelectContent>
                  {(countriesQuery.data ?? []).map((country) => (
                    <SelectItem key={String(country.id)} value={String(country.id)}>
                      {country.country_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {countriesQuery.isError && !errors.country_id ? <FieldError message="Unable to load countries right now." /> : null}
              <FieldError message={errors.country_id?.message} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="city">City</Label>
              <IconInput id="city" icon={MapPin} placeholder="London" disabled={isLoading} {...register('city')} aria-invalid={!!errors.city} />
              <FieldError message={errors.city?.message} />
            </div>
          </div>
        </FormSection>

        <FormSection step={2} title="Your details">
          <div className="space-y-1.5">
            <Label htmlFor="owner_name">Full name</Label>
            <IconInput id="owner_name" icon={User} placeholder="Jane Doe" autoComplete="name" disabled={isLoading} {...register('owner_name')} aria-invalid={!!errors.owner_name} />
            <FieldError message={errors.owner_name?.message} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <IconInput id="email" icon={Mail} type="email" placeholder="name@example.com" autoComplete="email" disabled={isLoading} {...register('email')} aria-invalid={!!errors.email} />
              <FieldError message={errors.email?.message} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone_number">Phone number</Label>
              <PhoneInput id="phone_number" dialCode={selectedCountry?.dial_code} placeholder={PHONE_NUMBER_SAMPLE_PLACEHOLDER} disabled={isLoading} {...register('phone_number')} aria-invalid={!!errors.phone_number} />
              <FieldError message={errors.phone_number?.message} />
            </div>
          </div>
        </FormSection>

        <FormSection step={3} title="Secure your account">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" placeholder="Create a password" disabled={isLoading} {...register('password')} aria-invalid={!!errors.password} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password_confirm">Confirm password</Label>
              <PasswordInput id="password_confirm" placeholder="Repeat your password" disabled={isLoading} {...register('password_confirm')} aria-invalid={!!errors.password_confirm} />
              <FieldError message={errors.password_confirm?.message} />
            </div>
          </div>

          <PasswordChecklist rules={PASSWORD_RULES} value={password} showErrors={Boolean(errors.password)} />
        </FormSection>

        <div className="space-y-4">
          <AuthTermsDisclosure termsText={null} privacyText={null} />

          <Button type="submit" size="lg" className="h-12 w-full rounded-xl text-base font-semibold shadow-lg shadow-primary/20" disabled={isLoading}>
            {isLoading ? <Spinner className="mr-2" /> : null}
            {isLoading ? 'Creating your community…' : 'Create community'}
            {isLoading ? null : <ArrowRight className="ml-1 h-4 w-4" />}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Joining an existing community?{' '}
            <Link to="/signup" className="font-semibold text-primary hover:underline">
              Sign up as a member
            </Link>
          </p>
        </div>
      </form>
    </SignupSplitShell>
  )
}
